import React, { useEffect, useMemo, useState } from "react";
import { FiArrowUpRight, FiX } from "react-icons/fi";
import { HiArrowTrendingUp } from "react-icons/hi2";

const API =
  process.env.REACT_APP_NAV_API ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:8080";

function getInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function formatAmountINR(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN")}`;
}

function safePayload(payload) {
  if (!payload) return {};
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload;
}

export default function SalesAchivementPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);

  async function load() {
    setLoading(true);
    setErr("");
    try {
const res = await fetch(
  `${API}/posts?status=PUBLISHED&post_type=SALES_ACHIEVEMENT&limit=100`,
  {
    method: "GET",
    credentials: "include", // ✅ ADD THIS
    headers: {
      "Content-Type": "application/json",
    },
  }
);
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json?.error || "Failed to load sales achievements");
      }

      const rows = Array.isArray(json.data) ? json.data : [];
      const normalized = rows.map((r) => ({
        ...r,
        payload: safePayload(r.payload),
      }));

      setItems(normalized);
    } catch (e) {
      setErr(e?.message || "Failed to load");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const countText = useMemo(() => {
    // return `${items.length} record${items.length === 1 ? "" : "s"}`;
  }, [items.length]);

  return (
    <>
      <div className="salesAchievementCard">
        <div className="salesAchievementHeader">
          <div className="salesAchievementHeaderLeft">
            <div className="salesAchievementHeaderIcon"> <HiArrowTrendingUp size={25} /></div>
            <div>
              <div className="salesAchievementTitle">Business Achievements</div>
              <div className="salesAchievementCount">{countText}</div>
            </div>
          </div>
        </div>

        <div className="salesAchievementDivider" />

        <div className="salesAchievementBody">
          {loading ? (
            <div className="salesAchievementState">Loading...</div>
          ) : err ? (
            <div className="salesAchievementState salesAchievementError">
              {err}
            </div>
          ) : !items.length ? (
            <div className="salesAchievementState">No sales achievements found.</div>
          ) : (
            <div className="salesAchievementList">
              {items.map((item) => {
                const p = safePayload(item.payload);
                const name = item.person_name || "Unnamed";
                const deptLoc = [item.department, item.location]
                  .filter(Boolean)
                  .join(" • ");

                return (
                  <button
                    key={item.post_id}
                    className="salesAchievementRow"
                    onClick={() => setActive(item)}
                    title="View details"
                  >
                    <div className="salesAchievementRowLeft">
                      {item.cover_image_url ? (
                        <img
                          src={item.cover_image_url}
                          alt={name}
                          className="salesAchievementAvatarImg"
                        />
                      ) : (
                        <div className="salesAchievementAvatar">
                          {getInitials(name)}
                        </div>
                      )}

                      <div className="salesAchievementMeta">
                        <div className="salesAchievementPerson">{name}</div>
                        <div className="salesAchievementDeptLoc">
                          {deptLoc || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="salesAchievementRowRight">
                      <div className="salesAchievementAmount">
                        {formatAmountINR(p.amount)}
                      </div>

                      <div className="salesAchievementTags">
                        {p.product ? (
                          <span className="salesAchievementTag">{p.product}</span>
                        ) : null}
                        {p.month ? (
                          <span className="salesAchievementTag salesAchievementTagSoft">
                            {p.month}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="salesAchievementArrowWrap">
                      <FiArrowUpRight size={16} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {active ? (
        <div
          className="salesAchModalBackdrop"
          onClick={() => setActive(null)}
        >
          <div
            className="salesAchModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="salesAchModalTop">
              <div className="salesAchModalProfile">
                {active.cover_image_url ? (
                  <img
                    src={active.cover_image_url}
                    alt={active.person_name || "Profile"}
                    className="salesAchModalAvatarImg"
                  />
                ) : (
                  <div className="salesAchModalAvatar">
                    {getInitials(active.person_name || "")}
                  </div>
                )}

                <div>
                  <div className="salesAchModalPerson">
                    {active.person_name || "Unnamed"}
                  </div>
                  <div className="salesAchModalDeptLoc">
                    {[active.department, active.location].filter(Boolean).join(" • ") || "—"}
                  </div>
                </div>
              </div>

              <button
                className="salesAchModalClose"
                onClick={() => setActive(null)}
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="salesAchModalDivider" />

            <div className="salesAchModalBody">
              <div className="salesAchModalTitle">{active.title}</div>
              <div className="salesAchModalDesc">{active.description}</div>

              <div className="salesAchModalStats">
                <div className="salesAchStatBox">
                  <div className="salesAchStatLabel">Amount</div>
                  <div className="salesAchStatValue">
                    {formatAmountINR(safePayload(active.payload).amount)}
                  </div>
                </div>

                <div className="salesAchStatBox">
                  <div className="salesAchStatLabel">Month</div>
                  <div className="salesAchStatValue">
                    {safePayload(active.payload).month || "—"}
                  </div>
                </div>

                <div className="salesAchStatBox">
                  <div className="salesAchStatLabel">Product</div>
                  <div className="salesAchStatValue">
                    {safePayload(active.payload).product || "—"}
                  </div>
                </div>

                {safePayload(active.payload).rank ? (
                  <div className="salesAchStatBox">
                    <div className="salesAchStatLabel">Rank</div>
                    <div className="salesAchStatValue">
                      {safePayload(active.payload).rank}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <button
              className="salesAchModalBtn"
              onClick={() => setActive(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}